import config from 'config';

import { getEffectiveSkuFromPermalink } from '~/grid/utils/cioUtils';
import { getBrandValue } from '~/shared/utils/brandNameHelper';

const BASE_CDN_URL = config.get('public.cdn');
const BASE_IMAGE_SERVICE_URL = `${BASE_CDN_URL}/assets/images/skus`;
const BASE_CDN_IMAGE_URL = `${BASE_CDN_URL}/media/images/products`;
const BASE_CDN_SWATCH_URL = `${BASE_CDN_URL}/media/images/products`;

export const PRODUCT_IMAGE_SIZES = {
  DEFAULT: {
    MOBILE: { width: 327, height: 491 },
    DESKTOP: { width: 577, height: 866 },
  },
  SAVAGE: {
    MOBILE: { width: 600, height: 800 },
    DESKTOP: { width: 600, height: 800 },
  },
};

export const PRODUCT_IMAGE_ANGLE_TYPES = {
  PRIMARY: '1',
  SECONDARY: '2',
  LAYDOWN: 'LAYDOWN',
};

/**
 * Returns the product image size width based on the device type (mobile or desktop).
 *
 * @param {boolean} isMobile - Indicates if the device is mobile.
 * @returns {number} - The width of the product image.
 */
export const getProductImageSizeWidth = (isMobile) =>
  isMobile
    ? getBrandValue({
        savage: PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.width,
        default: PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.width,
      })
    : getBrandValue({
        savage: PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.width,
        default: PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.width,
      });

/**
 * Returns the product image size height based on the device type (mobile or desktop).
 *
 * @param {boolean} isMobile - Indicates if the device is mobile.
 * @returns {number} - The height of the product image.
 */
export const getProductImageSizeHeight = (isMobile) =>
  isMobile
    ? getBrandValue({
        savage: PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.height,
        default: PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.height,
      })
    : getBrandValue({
        savage: PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.height,
        default: PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.height,
      });

/**
 * Returns the product image size string based on the device type (mobile or desktop).
 *
 * @param {boolean} isMobile - Indicates if the device is mobile.
 * @returns {string} - The size string of the product image in the format "widthxheight".
 */
export const getProductImageSizeString = (isMobile) =>
  isMobile
    ? getBrandValue({
        savage: `${PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.width}x${PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.height}`,
        default: `${PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.width}x${PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.height}`,
      })
    : getBrandValue({
        savage: `${PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.width}x${PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.height}`,
        default: `${PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.width}x${PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.height}`,
      });

/**
 * Returns the product image aspect ratio based on the device type (mobile or desktop).
 *
 * @param {boolean} isMobile - Indicates if the device is mobile.
 * @returns {number} - The calculated aspect ratio of the product image.
 */
export const getProductImageAspectRatio = (isMobile) =>
  isMobile
    ? getBrandValue({
        savage:
          PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.width /
          PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.height,
        default:
          PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.width /
          PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.height,
      })
    : getBrandValue({
        savage:
          PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.width /
          PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.height,
        default:
          PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.width /
          PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.height,
      });

/**
 * Returns the product image aspect ratio string based on the device type (mobile or desktop).
 *
 * @param {boolean} isMobile - Indicates if the device is mobile.
 * @returns {string} - The aspect ratio string of the product image in the format "width/height".
 */
export const getProductImageAspectRatioString = (isMobile) =>
  isMobile
    ? getBrandValue({
        savage: `${PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.width}/${PRODUCT_IMAGE_SIZES.SAVAGE.MOBILE.height}`,
        default: `${PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.width}/${PRODUCT_IMAGE_SIZES.DEFAULT.MOBILE.height}`,
      })
    : getBrandValue({
        savage: `${PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.width}/${PRODUCT_IMAGE_SIZES.SAVAGE.DESKTOP.height}`,
        default: `${PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.width}/${PRODUCT_IMAGE_SIZES.DEFAULT.DESKTOP.height}`,
      });

/**
 * Builds the swatch image URL from the given SKU.
 * Necessary for Cio product data due to Cio not having swatch images.
 *
 * @param {string} sku - The SKU of the product.
 * @param {string} permalink - The permalink of the product.
 * @returns {string} - The swatch image URL.
 */
export const buildSwatchImageUrlFromSku = (sku, permalink) => {
  const effectiveSku = sku || getEffectiveSkuFromPermalink({ permalink });

  const swatchUrl = getBrandValue({
    savage: `${BASE_CDN_SWATCH_URL}/${effectiveSku}/${effectiveSku}-SWATCH.jpg`,
    default: `${BASE_CDN_SWATCH_URL}/${effectiveSku}/${effectiveSku}-swatch_1_36x36.jpg`,
  });
  return swatchUrl;
};

/**
 * Builds the image service URL from the given SKU.
 * Necessary for Products/v2 product data due to Products/v2 not yet supporting image service URLs.
 * This can be deprecated once we expose image service URLs in Products/v2.
 *
 * @param {string} sku - The SKU of the product.
 * @returns {string|null} - The image service URL or null if SKU is not provided.
 */
export const buildImageServiceUrlFromSku = (sku) => {
  if (!sku) {
    return null;
  }
  return `${BASE_IMAGE_SERVICE_URL}/${sku}`;
};

/**
 * Builds the image service URL from the given item number.
 * Necessary for related product data due to GQL not yet supporting image service URLs for related products.
 * This can be deprecated once we fully migrate to the new image service.
 *
 * @param {string} itemNumber - The item number of the product.
 * @returns {string|null} - The image service URL or null if item number is not provided.
 */
export const buildImageServiceUrlFromItemNumber = (itemNumber) => {
  if (!itemNumber) {
    return null;
  }
  const sku = itemNumber?.replace(/-00$/g, '');
  return `${BASE_IMAGE_SERVICE_URL}/${sku}`;
};

/**
 * Checks if the plus image URL exists by sending a HEAD request.
 * If the URL is valid, it returns true; otherwise, it returns false.
 * We need to do this because the plus image URL is not always available.
 * This can be deprecated once we fully migrate to the new image service.
 *
 * @param {string} url - The URL to check.
 * @returns {Promise<boolean>} - Returns true if the URL exists, false otherwise.
 */
const checkIfPlusUrlExists = async (url) => {
  const response = await fetch(url, { method: 'HEAD' });
  return response.ok;
};

/**
 * Builds the image URL from the given SKU, requested angle, and requested size.
 * This is used for the product card image after the initial load, since this is an
 * async function that fetches the image URL. Works together with `buildDefaultImageUrlFromSku()`
 * This can be deprecated once we fully migrate to the new image service.
 *
 * @param {string} sku - The SKU of the product.
 * @param {string} permalink - The permalink of the product.
 * @param {boolean} showPlusSizeImage - Indicates if the plus size image should be shown.
 * @param {string} requestedAngle - The requested angle of the image (e.g., '1', '2').
 * @param {string} requestedSize - The requested size of the image (e.g., '327x491', '577x866').
 * @returns {Promise<string|null>} - The image URL or null if SKU is not provided.
 */
export const buildImageUrlFromSku = async ({
  sku,
  permalink,
  showPlusSizeImage,
  requestedAngle,
  requestedSize,
}) => {
  if (!sku) {
    return null;
  }

  const plusSuffix = '-plus';
  const defaultImageUrl = getBrandValue({
    savage: `${BASE_CDN_IMAGE_URL}/${sku.toUpperCase()}/${permalink.toUpperCase()}-${requestedAngle}-${requestedSize}.jpg`,
    default: `${BASE_CDN_IMAGE_URL}/${sku}/${permalink}-${requestedAngle}_${requestedSize}.jpg`,
  });

  if (showPlusSizeImage) {
    const plusImageUrl = getBrandValue({
      savage: defaultImageUrl, // savage does not have plus size images, so we fallback to the default image URL
      default: `${BASE_CDN_IMAGE_URL}/${sku}/${permalink}-${requestedAngle}_${requestedSize}${plusSuffix}.jpg`,
    });
    const plusImageExists = await checkIfPlusUrlExists(plusImageUrl);
    return plusImageExists ? plusImageUrl : defaultImageUrl;
  }

  return `https:${defaultImageUrl}`;
};

/**
 * Builds the default image URL from the given SKU, requested angle, and requested size.
 * This is used for the product card image on initial load to avoid flickering.
 * Works together with `buildImageUrlFromSku()`.
 * This can be deprecated once we fully migrate to the new image service.
 *
 * @param {string} sku - The SKU of the product.
 * @param {string} permalink - The permalink of the product.
 * @param {string} requestedAngle - The requested angle of the image (e.g., '1', '2').
 * @param {string} requestedSize - The requested size of the image (e.g., '327x491', '577x866').
 * @returns {string|null} - The default image URL or null if SKU is not provided.
 */
export const buildDefaultImageUrlFromSku = ({
  sku,
  permalink,
  requestedAngle,
  requestedSize,
}) => {
  if (!sku) {
    return null;
  }
  const effectiveSku = sku || getEffectiveSkuFromPermalink({ permalink });

  const defaultImageUrl = getBrandValue({
    savage: `${BASE_CDN_IMAGE_URL}/${effectiveSku.toUpperCase()}/${permalink.toUpperCase()}-${requestedAngle}-${requestedSize}.jpg`,
    default: `${BASE_CDN_IMAGE_URL}/${effectiveSku}/${permalink}-${requestedAngle}_${requestedSize}.jpg`,
  });

  return defaultImageUrl;
};

/**
 * Builds the image URL from the given image set, requested angle, and requested size.
 * This use used for related products and can be deprecated once we fully migrate to the new image service.
 *
 * @param {Object} imageSet - The image set object containing image data.
 * @param {boolean} showPlusSizeImage - Indicates if the plus size image should be shown.
 * @param {string} requestedAngle - The requested angle of the image (e.g., '1', '2').
 * @param {string} requestedWidth - The requested width of the image (e.g., '327', '577').
 * @param {string} requestedHeight - The requested height of the image (e.g., '491', '866').
 * @returns {string|null} - The image URL or null if image set is not provided.
 */
export const getImageUrlFromImageSet = ({
  imageSet,
  showPlusSizeImage,
  requestedAngle,
  requestedWidth,
  requestedHeight,
}) => {
  if (!imageSet?.default?.length) {
    return null;
  }

  // Select image set based on profile/selected size (default/plus)
  const imageAngleSet =
    showPlusSizeImage && imageSet?.plus?.length
      ? imageSet?.plus
      : imageSet?.default;

  // Select image set based on requested angle (primary/hover)
  const requestedImageSizeSet = imageAngleSet?.find(
    ({ angle }) => angle === requestedAngle
  );
  const fallbackImageSizeSet = imageAngleSet?.find(
    ({ angle }) => angle === '1'
  );
  const imageSizeSet =
    requestedImageSizeSet?.sizes || fallbackImageSizeSet?.sizes;

  // Select image size based on requested width and height (desktop/mobile)
  const requestedImageViewportSizeSet = imageSizeSet?.find(
    ({ width, height }) =>
      width === requestedWidth && height === requestedHeight
  );
  const fallbackImageViewportSizeSet = imageSizeSet?.at(-1);
  const imageUrl =
    requestedImageViewportSizeSet?.url || fallbackImageViewportSizeSet?.url;

  if (!imageUrl) {
    return null;
  }

  return `https://${imageUrl}`;
};

/**
 * Builds the fallback image URL from the CDN. This is used when the image fails to load.
 * Fallback images have just 1 angle (no separate hover image) and do not distinguish between default and plus.
 * Test the onError functionality by setting the image src to the following to trigger a bad url
 * "https://cdn.fabletics.com/assets/images/skuss/JT2458227-9299asdf"
 *
 * @param {Object} options
 * @param {string} options.requestedSize - The requested size of the image in "widthxheight" format (e.g., '327x491', '577x866').
 *   Use `getProductImageSizeString(isMobile)` to get the correct value.
 * @returns {string} - The fallback image URL.
 */
export const buildFallbackImageFromCDN = ({ requestedSize }) => {
  return getBrandValue({
    savage: `${BASE_CDN_URL}/media/images/no_image_returned_${requestedSize}.png`,
    default: `${BASE_CDN_IMAGE_URL}/image_service/no_image_returned_${requestedSize}.png`,
  });
};
